import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';
import '../styles/ai-integration.css';

const AIIntegration = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    name: '',
    permissions: [],
    expiresInDays: 30
  });
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    if (isOpen && user) {
      loadTokens();
      loadUserPermissions();
    }
  }, [isOpen, user]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/mcp-token?operation=list&userId=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens || []);
      } else {
        console.error('Failed to load tokens');
      }
    } catch (error) {
      console.error('Error loading tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.permissions || []);
        setTokenForm(prev => ({ ...prev, permissions: data.permissions || [] }));
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
  };

  const generateToken = async () => {
    setGenerating(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/mcp-token?operation=generate&userId=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenForm),
      });

      if (response.ok) {
        const data = await response.json();
        setNewToken(data);
        setShowTokenForm(false);
        await loadTokens();
      } else {
        const errorData = await response.json();
        alert(`Failed to generate token: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      alert('Failed to generate token. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const revokeToken = async (tokenId) => {
    if (!window.confirm(t('aiIntegration.confirmRevoke'))) {
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/mcp-token?operation=revoke&userId=${user.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tokenId }),
      });

      if (response.ok) {
        await loadTokens();
      } else {
        alert('Failed to revoke token');
      }
    } catch (error) {
      console.error('Error revoking token:', error);
      alert('Failed to revoke token');
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    alert(t('aiIntegration.tokenCopied'));
  };

  const togglePermission = (permission) => {
    setTokenForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const getPermissionDisplayName = (permission) => {
    const displayNames = {
      'manage-rehearsals': 'Manage Rehearsals',
      'manage-news': 'Manage News',
      'manage-users': 'Manage Users',
      'view-members': 'View Members',
      'manage-tips': 'Manage Tips',
      'use-player': 'Use Music Player',
      'save-settings': 'Save Settings'
    };
    return displayNames[permission] || permission;
  };

  if (!isOpen) return null;

  return (
    <div className="ai-integration-overlay">
      <div className="ai-integration-modal">
        <div className="ai-integration-header">
          <div className="header-content">
            <div className="header-icon">🤖</div>
            <div className="header-text">
              <h2>{t('aiIntegration.title')}</h2>
              <p>{t('aiIntegration.subtitle')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="ai-integration-content">
          {/* Setup Instructions */}
          <div className="setup-instructions">
            <h3>{t('aiIntegration.setupTitle')}</h3>
            <ol>
              <li>{t('aiIntegration.step1')}</li>
              <li>{t('aiIntegration.step2')}</li>
              <li>{t('aiIntegration.step3')}</li>
              <li>{t('aiIntegration.step4')}</li>
            </ol>
          </div>

          {/* New Token Button */}
          <div className="token-actions">
            <button 
              className="generate-token-btn"
              onClick={() => setShowTokenForm(true)}
              disabled={generating}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
              {t('aiIntegration.generateToken')}
            </button>
          </div>

          {/* Token Generation Form */}
          {showTokenForm && (
            <div className="token-form">
              <h3>{t('aiIntegration.newTokenTitle')}</h3>
              
              <div className="form-group">
                <label>{t('aiIntegration.tokenName')}</label>
                <input
                  type="text"
                  value={tokenForm.name}
                  onChange={(e) => setTokenForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('aiIntegration.tokenNamePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('aiIntegration.expiresIn')}</label>
                <select
                  value={tokenForm.expiresInDays}
                  onChange={(e) => setTokenForm(prev => ({ ...prev, expiresInDays: parseInt(e.target.value) }))}
                >
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('aiIntegration.permissions')}</label>
                <div className="permissions-list">
                  {userPermissions.map(permission => (
                    <label key={permission} className="permission-item">
                      <input
                        type="checkbox"
                        checked={tokenForm.permissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span>{getPermissionDisplayName(permission)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button 
                  onClick={() => setShowTokenForm(false)}
                  className="cancel-btn"
                >
                  {t('controls.cancel')}
                </button>
                <button 
                  onClick={generateToken}
                  className="generate-btn"
                  disabled={generating || !tokenForm.name}
                >
                  {generating ? t('aiIntegration.generating') : t('aiIntegration.generate')}
                </button>
              </div>
            </div>
          )}

          {/* New Token Display */}
          {newToken && (
            <div className="new-token-display">
              <h3>{t('aiIntegration.tokenGenerated')}</h3>
              <div className="token-container">
                <div className="token-value">
                  <code>{newToken.token}</code>
                </div>
                <button 
                  onClick={() => copyToken(newToken.token)}
                  className="copy-btn"
                >
                  {t('aiIntegration.copy')}
                </button>
              </div>
              <div className="token-warning">
                ⚠️ {t('aiIntegration.tokenWarning')}
              </div>
              <button 
                onClick={() => setNewToken(null)}
                className="dismiss-btn"
              >
                {t('aiIntegration.dismiss')}
              </button>
            </div>
          )}

          {/* Existing Tokens */}
          <div className="existing-tokens">
            <h3>{t('aiIntegration.existingTokens')}</h3>
            {loading ? (
              <div className="loading">{t('aiIntegration.loadingTokens')}</div>
            ) : tokens.length === 0 ? (
              <div className="no-tokens">{t('aiIntegration.noTokens')}</div>
            ) : (
              <div className="tokens-list">
                {tokens.map(token => (
                  <div key={token.id} className={`token-item ${token.is_expired ? 'expired' : ''}`}>
                    <div className="token-info">
                      <div className="token-name">{token.name}</div>
                      <div className="token-meta">
                        <span className="token-permissions">
                          {token.permissions.length} {t('aiIntegration.permissionsCount')}
                        </span>
                        <span className="token-expires">
                          {token.is_expired ? t('aiIntegration.expired') : 
                           `${t('aiIntegration.expires')} ${new Date(token.expires_at).toLocaleDateString()}`}
                        </span>
                      </div>
                      <div className="token-created">
                        {t('aiIntegration.created')} {new Date(token.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="token-actions-inline">
                      <button 
                        onClick={() => revokeToken(token.id)}
                        className="revoke-btn"
                      >
                        {t('aiIntegration.revoke')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIIntegration;